// Email passwordless login — three endpoints that map 1:1 to the backend spec.
//
// Flow: requestEmailCode → user gets email with code + magic link →
//   Path A: verifyEmailCode (user types 4-digit code)
//   Path B: verifyEmailLink (user clicks magic link, lands on /profile?token=...)
//
// Whichever path is used first wins. The other becomes invalid.

import { apiFetch, ApiError } from "./apiClient";
import { StoredUser } from "../utils/authStorage";

const EMAIL_AUTH_PATH = "/v1/auth/email";

export interface EmailAuthResult {
  token: string;
  user: StoredUser;
}

interface EmailVerifyResponse {
  access_token: string;
  token_type: string;
  name: string | null;
  email: string;
}

/**
 * POST /email/request — sends login email with code + magic link.
 * Always returns 204, never reveals whether the email exists.
 */
export const requestEmailCode = async (email: string): Promise<void> => {
  await apiFetch<void>(`${EMAIL_AUTH_PATH}/request`, {
    method: "POST",
    body: { email, app: "capcam" },
  });
};

/**
 * POST /email/verify-code — exchanges 4-digit code for JWT.
 * Throws ApiError on 401 (bad code), 422 (malformed), 429 (locked).
 */
export const verifyEmailCode = async (
  email: string,
  code: string
): Promise<EmailAuthResult> => {
  const res = await apiFetch<EmailVerifyResponse>(
    `${EMAIL_AUTH_PATH}/verify-code`,
    {
      method: "POST",
      body: { email, app: "capcam", code },
    }
  );
  return normalizeResult(res);
};

/**
 * POST /email/verify-link — exchanges magic-link token for JWT.
 * Throws ApiError on 401 (bad/expired/consumed), 422 (too short).
 */
export const verifyEmailLink = async (
  token: string
): Promise<EmailAuthResult> => {
  const res = await apiFetch<EmailVerifyResponse>(
    `${EMAIL_AUTH_PATH}/verify-link`,
    {
      method: "POST",
      body: { token },
    }
  );
  return normalizeResult(res);
};

const normalizeResult = (res: EmailVerifyResponse): EmailAuthResult => {
  if (!res?.access_token) {
    throw new Error("Auth backend did not return an access_token");
  }
  return {
    token: res.access_token,
    user: {
      name: res.name ?? "",
      email: res.email ?? "",
    },
  };
};

/**
 * Maps ApiError status codes to user-facing i18n keys for the email flow.
 */
export const getEmailErrorKey = (err: unknown): string => {
  if (err instanceof ApiError) {
    if (err.status === 429) return "emailCodeLocked";
    if (err.status === 401) return "emailCodeInvalid";
    if (err.status === 422) return "emailCodeMalformed";
  }
  return "loginNetworkError";
};
